import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/context/AuthContext";
import {
  Monitor, Search, ExternalLink, ChevronDown, ChevronUp,
  BarChart2, Grid3X3, Map, RefreshCw, Building2, Package, Zap,
  AlertTriangle, CheckCircle2, Clock, Layers, Users, TrendingUp,
  Euro, X, Code2, Plus, Edit2, Trash2, Save, Loader2,
  Globe, Wifi, WifiOff, Tag, Calendar, Link
} from "lucide-react";
import AttachCostModal from "@/components/AttachCostModal";

// ── Helpers ────────────────────────────────────────────────────────────────────
const IRA_CONFIG = {
  "Producto reusable":       { color: "bg-emerald-100 text-emerald-800 border-emerald-200", bar: "bg-emerald-500", dot: "bg-emerald-500" },
  "Reusable con variantes":  { color: "bg-blue-100 text-blue-800 border-blue-200",          bar: "bg-blue-500",    dot: "bg-blue-500" },
  "Solución local":          { color: "bg-amber-100 text-amber-800 border-amber-200",        bar: "bg-amber-500",   dot: "bg-amber-500" },
};

const LINEA_COLORS = {
  "Movilidad":                     { bg: "bg-blue-50",   border: "border-blue-200",   text: "text-blue-700",   accent: "#3B82F6" },
  "Gestión Residuos y Limpieza":   { bg: "bg-green-50",  border: "border-green-200",  text: "text-green-700",  accent: "#22C55E" },
  "Zonas Verdes e Infraestructura":{ bg: "bg-teal-50",   border: "border-teal-200",   text: "text-teal-700",   accent: "#14B8A6" },
  "Control Presencia y RRHH":      { bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-700", accent: "#A855F7" },
  "Parques Urbanos":               { bg: "bg-lime-50",   border: "border-lime-200",   text: "text-lime-700",   accent: "#84CC16" },
  "Transversal":                   { bg: "bg-gray-50",   border: "border-gray-200",   text: "text-gray-700",   accent: "#6B7280" },
};

const LINEA_ORDER = ["Movilidad", "Gestión Residuos y Limpieza", "Zonas Verdes e Infraestructura", "Control Presencia y RRHH", "Parques Urbanos", "Transversal"];

const TEC_BADGE = {
  ".NET":           "bg-indigo-100 text-indigo-800",
  ".NET 8":         "bg-indigo-100 text-indigo-800",
  ".NET / Azure ML":"bg-violet-100 text-violet-800",
  "Legacy":         "bg-orange-100 text-orange-800",
  "Legacy / Inca":  "bg-orange-100 text-orange-800",
};

function fmtEur(n) {
  if (!n) return "—";
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
}

function IraBadge({ tipo, score }) {
  const cfg = IRA_CONFIG[tipo] || IRA_CONFIG["Solución local"];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${cfg.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      IRA {score} · {tipo}
    </span>
  );
}

// ── App Card ──────────────────────────────────────────────────────────────────
function AppCard({ app, onEdit, onDelete, isAdmin }) {
  const [expanded, setExpanded] = useState(false);
  const [showCost, setShowCost] = useState(false);
  const linea = LINEA_COLORS[app.linea_servicio] || LINEA_COLORS["Transversal"];
  const tecClass = TEC_BADGE[app.tecnologia] || "bg-gray-100 text-gray-700";

  return (
    <div
      data-testid={`app-card-${app.id}`}
      className={`rounded-xl border ${linea.border} ${linea.bg} overflow-hidden transition-all duration-200 hover:shadow-md`}
    >
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 text-sm leading-tight">{app.nombre}</h3>
            <p className={`text-xs font-medium mt-0.5 ${linea.text}`}>{app.linea_servicio}</p>
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${tecClass}`}>
              {app.tecnologia}
            </span>
            <div className="flex items-center gap-1">
              {isAdmin && (
                <>
                  <button onClick={() => onEdit(app)} className="text-gray-300 hover:text-[#007934] transition-colors p-0.5" data-testid={`edit-app-${app.id}`} title="Editar">
                    <Edit2 size={11} />
                  </button>
                  <button onClick={() => onDelete(app)} className="text-gray-300 hover:text-red-500 transition-colors p-0.5" data-testid={`delete-app-${app.id}`} title="Eliminar">
                    <Trash2 size={11} />
                  </button>
                </>
              )}
              {app.link_devops && (
                <a href={app.link_devops} target="_blank" rel="noopener noreferrer"
                  className="text-gray-400 hover:text-blue-500 transition-colors"
                  title="Ver proyecto en Azure DevOps (requiere acceso corporativo)"
                  data-testid={`app-devops-link-${app.id}`}>
                  <ExternalLink size={12} />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* IRA Score bar */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <IraBadge tipo={app.ira_tipo} score={app.ira_score} />
            <span className={`text-xs ${app.estado === "deprecado" ? "text-red-500" : app.estado === "mantenimiento" ? "text-amber-600" : "text-emerald-600"} font-medium`}>
              {app.estado === "deprecado" ? "Deprecado" : app.estado === "mantenimiento" ? "Mantenimiento" : "Activo"}
            </span>
          </div>
          <div className="w-full bg-white/60 rounded-full h-1.5 mt-1">
            <div
              className={`h-1.5 rounded-full transition-all ${(IRA_CONFIG[app.ira_tipo] || IRA_CONFIG["Solución local"]).bar}`}
              style={{ width: `${Math.min(app.ira_score, 100)}%` }}
            />
          </div>
        </div>

        {/* Description */}
        <p className="text-xs text-gray-600 line-clamp-2 mb-3">{app.descripcion}</p>

        {/* Quick stats */}
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <Building2 size={11} />
            {app.municipios?.length || 0} municipios
          </span>
          <span className="flex items-center gap-1">
            <Users size={11} />
            {app.equipo}
          </span>
          {app.costes?.coste_anual > 0 && (
            <span className="flex items-center gap-1 font-semibold text-gray-700">
              {fmtEur(app.costes.coste_anual)} /año
            </span>
          )}
        </div>
      </div>

      {/* Expandable detail */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-2 bg-white/40 hover:bg-white/60 transition-colors border-t border-white/50 text-xs text-gray-500 font-medium"
        data-testid={`app-expand-${app.id}`}
      >
        <span>Ver detalle</span>
        {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 bg-white/30 space-y-3">
          {/* Descripción */}
          {app.descripcion && (
            <div>
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1">Descripción</p>
              <p className="text-[11px] text-gray-700 leading-relaxed">{app.descripcion}</p>
            </div>
          )}

          {/* Municipios */}
          {app.municipios?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1.5">Municipios</p>
              <div className="flex flex-wrap gap-1">
                {app.municipios.map(m => (
                  <span key={m} className="text-[10px] bg-white/70 border border-gray-200 px-1.5 py-0.5 rounded text-gray-700">
                    {m}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Módulos */}
          {app.modulos?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1.5">Módulos principales</p>
              <div className="flex flex-wrap gap-1">
                {app.modulos.slice(0, 6).map(m => (
                  <span key={m} className="text-[10px] bg-white/70 border border-gray-200 px-1.5 py-0.5 rounded text-gray-700">
                    {m}
                  </span>
                ))}
                {app.modulos.length > 6 && (
                  <span className="text-[10px] text-gray-400">+{app.modulos.length - 6} más</span>
                )}
              </div>
            </div>
          )}

          {/* Integraciones */}
          {app.integraciones?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1.5">Integraciones</p>
              <div className="flex flex-wrap gap-1">
                {app.integraciones.map(i => (
                  <span key={i} className="text-[10px] bg-blue-50 border border-blue-200 text-blue-700 px-1.5 py-0.5 rounded font-medium">
                    {i}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* IRA detalle */}
          {app.ira_detalle && (
            <div>
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1.5">Métricas IRA</p>
              <div className="grid grid-cols-2 gap-1 text-[10px] text-gray-600">
                <span>Reutilización funcional: <b>{app.ira_detalle.reutilizacion_funcional}%</b></span>
                <span>Núcleo común: <b>{app.ira_detalle.nucleo_comun}%</b></span>
                <span>Homogeneidad: <b>{app.ira_detalle.homogeneidad}%</b></span>
                <span>Horas despliegue: <b>{app.ira_detalle.horas_despliegue_nueva_poblacion}h</b></span>
              </div>
            </div>
          )}

          {/* Proveedor */}
          <div className="flex items-center gap-2 text-xs text-gray-500 pt-1 border-t border-white/50">
            <Package size={11} />
            <span>Proveedor: <strong className="text-gray-700">{app.proveedor}</strong></span>
            {app.actividad && (
              <>
                <span className="text-gray-300">|</span>
                <Clock size={11} />
                <span>Actividad: {app.actividad}</span>
              </>
            )}
          </div>

          {/* Llevar a coste en licitación */}
          <button
            onClick={(e) => { e.stopPropagation(); setShowCost(true); }}
            className="w-full flex items-center justify-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg py-1.5 text-[11px] font-semibold transition-colors"
            data-testid={`app-cost-btn-${app.id}`}
            title="Añadir como CAPEX/OPEX al presupuesto de una licitación en preparación"
          >
            <Euro size={12} /> Llevar a coste en licitación
          </button>
        </div>
      )}
      <AttachCostModal
        open={showCost}
        onClose={() => setShowCost(false)}
        source={{
          type: "app",
          id: app.id,
          nombre: app.nombre,
          defaultConcepto: `${app.nombre} — licencia/operación`,
          defaultImporte: app.costes?.coste_anual || 0,
          defaultTipo: "opex",
          defaultUnidad: "año",
        }}
      />
    </div>
  );
}

// ── CRUD Form Modal ───────────────────────────────────────────────────────────
const EMPTY_FORM = {
  nombre: "", nombre_corto: "", linea_servicio: "Movilidad", descripcion: "",
  tecnologia: ".NET", proveedor: "Valoriza", ira_score: 50, estado: "activo",
  municipios: "", modulos: "", integraciones: "", link_devops: "",
  equipo: "", actividad: "", horas_mes: 0, coste_hora: 45,
};

function AppFormModal({ app, onClose, onSaved }) {
  const isEdit = !!app?.id;
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (app) {
      setForm({
        nombre: app.nombre || "",
        nombre_corto: app.nombre_corto || "",
        linea_servicio: app.linea_servicio || "Movilidad",
        descripcion: app.descripcion || "",
        tecnologia: app.tecnologia || ".NET",
        proveedor: app.proveedor || "Valoriza",
        ira_score: app.ira_score ?? 50,
        estado: app.estado || "activo",
        municipios: (app.municipios || []).join(", "),
        modulos: (app.modulos || []).join("\n"),
        integraciones: (app.integraciones || []).join(", "),
        link_devops: app.link_devops || "",
        equipo: app.equipo || "",
        actividad: app.actividad || "",
        horas_mes: app.costes?.horas_mes ?? 0,
        coste_hora: app.costes?.coste_hora ?? 45,
      });
    }
  }, [app]);

  const f = form;
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const ira_score = Number(f.ira_score) || 0;
  const ira_tipo = ira_score >= 90 ? "Producto reusable" : ira_score >= 50 ? "Reusable con variantes" : "Solución local";
  const coste_mes = Math.round((Number(f.horas_mes) || 0) * (Number(f.coste_hora) || 45));
  const coste_anual = coste_mes * 12;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        nombre: f.nombre,
        nombre_corto: f.nombre_corto || f.nombre.split("(")[0].trim(),
        linea_servicio: f.linea_servicio,
        descripcion: f.descripcion,
        tecnologia: f.tecnologia,
        proveedor: f.proveedor,
        ira_score,
        ira_tipo,
        estado: f.estado,
        municipios: f.municipios.split(",").map(s => s.trim()).filter(Boolean),
        modulos: f.modulos.split("\n").map(s => s.trim()).filter(Boolean),
        integraciones: f.integraciones.split(",").map(s => s.trim()).filter(Boolean),
        link_devops: f.link_devops,
        equipo: f.equipo,
        actividad: f.actividad,
        costes: { horas_mes: Number(f.horas_mes)||0, coste_hora: Number(f.coste_hora)||45, coste_mes, coste_anual },
      };
      if (isEdit) {
        await axios.put(`${API}/catalogo-apps/${app.id}`, payload);
      } else {
        await axios.post(`${API}/catalogo-apps`, payload);
      }
      onSaved();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const INPUT = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]";

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl my-4" data-testid="app-form-modal">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-bold font-['Outfit'] flex items-center gap-2">
            <Monitor size={18} className="text-[#007934]" />
            {isEdit ? `Editar: ${app.nombre_corto || app.nombre}` : "Nueva Aplicación"}
          </h2>
          <button onClick={onClose}><X size={20} className="text-gray-400" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          {/* Row 1: Nombre */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Nombre completo *</label>
              <input value={f.nombre} onChange={e => set("nombre", e.target.value)} required placeholder="Ej: Girasol (ORA / Movilidad)" className={INPUT} data-testid="app-nombre-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Nombre corto</label>
              <input value={f.nombre_corto} onChange={e => set("nombre_corto", e.target.value)} placeholder="Ej: Girasol" className={INPUT} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Estado</label>
              <select value={f.estado} onChange={e => set("estado", e.target.value)} className={INPUT} data-testid="app-estado-select">
                <option value="activo">Activo</option>
                <option value="mantenimiento">Mantenimiento</option>
                <option value="deprecado">Deprecado</option>
              </select>
            </div>
          </div>

          {/* Row 2: Línea, Tecnología, Proveedor */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Línea de servicio *</label>
              <select value={f.linea_servicio} onChange={e => set("linea_servicio", e.target.value)} className={INPUT} data-testid="app-linea-select">
                {LINEA_ORDER.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Tecnología</label>
              <select value={f.tecnologia} onChange={e => set("tecnologia", e.target.value)} className={INPUT}>
                {[".NET", ".NET 8", "Legacy", "Legacy / Inca", ".NET / Azure ML"].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Proveedor</label>
              <input value={f.proveedor} onChange={e => set("proveedor", e.target.value)} className={INPUT} />
            </div>
          </div>

          {/* IRA Score */}
          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">IRA Score (0–100)</label>
              <input type="number" min={0} max={100} value={f.ira_score} onChange={e => set("ira_score", e.target.value)} className={INPUT} data-testid="app-ira-input" />
            </div>
            <div className="pb-2">
              <span className={`text-xs px-3 py-1 rounded-full font-semibold border ${(IRA_CONFIG[ira_tipo] || IRA_CONFIG["Solución local"]).color}`}>
                → {ira_tipo}
              </span>
            </div>
          </div>

          {/* Descripción */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Descripción</label>
            <textarea rows={3} value={f.descripcion} onChange={e => set("descripcion", e.target.value)} placeholder="Descripción de la aplicación y sus funcionalidades principales..." className={`${INPUT} resize-none`} data-testid="app-descripcion-textarea" />
          </div>

          {/* Municipios e Integraciones */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Municipios <span className="text-gray-400 font-normal">(separados por coma)</span></label>
              <textarea rows={2} value={f.municipios} onChange={e => set("municipios", e.target.value)} placeholder="Madrid, Barcelona, Bilbao..." className={`${INPUT} resize-none`} data-testid="app-municipios-textarea" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Integraciones <span className="text-gray-400 font-normal">(separadas por coma)</span></label>
              <textarea rows={2} value={f.integraciones} onChange={e => set("integraciones", e.target.value)} placeholder="MiNT, Azure ML, DGT..." className={`${INPUT} resize-none`} />
            </div>
          </div>

          {/* Módulos */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Módulos <span className="text-gray-400 font-normal">(uno por línea)</span></label>
            <textarea rows={3} value={f.modulos} onChange={e => set("modulos", e.target.value)} placeholder="Gestión ORA&#10;GDR.Residentes&#10;Dashboard KPIs" className={`${INPUT} resize-none font-mono text-xs`} />
          </div>

          {/* Costes */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Horas/mes</label>
              <input type="number" min={0} value={f.horas_mes} onChange={e => set("horas_mes", e.target.value)} className={INPUT} data-testid="app-horas-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">€/hora</label>
              <input type="number" min={0} value={f.coste_hora} onChange={e => set("coste_hora", e.target.value)} className={INPUT} />
            </div>
            <div className="pb-1 flex flex-col justify-end">
              <p className="text-xs text-gray-500">Est. mensual: <strong className="text-gray-800">{coste_mes.toLocaleString("es-ES")}€</strong></p>
              <p className="text-xs text-gray-500">Est. anual: <strong className="text-[#007934]">{coste_anual.toLocaleString("es-ES")}€</strong></p>
            </div>
          </div>

          {/* Equipo + DevOps */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Equipo</label>
              <input value={f.equipo} onChange={e => set("equipo", e.target.value)} placeholder="Ej: 3 personas" className={INPUT} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Link DevOps</label>
              <input value={f.link_devops} onChange={e => set("link_devops", e.target.value)} placeholder="https://dev.azure.com/..." className={INPUT} />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2 border-t border-gray-100">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-200 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">
              Cancelar
            </button>
            <button type="submit" disabled={saving || !f.nombre}
              className="flex-1 bg-[#007934] text-white rounded-xl py-2.5 text-sm font-semibold hover:bg-[#006528] disabled:opacity-60 flex items-center justify-center gap-2"
              data-testid="save-app-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear aplicación"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Matriz de Municipios ──────────────────────────────────────────────────────
function MatrizView({ data }) {
  if (!data) return <div className="flex items-center justify-center py-20 text-gray-400"><RefreshCw className="animate-spin mr-2" size={16} />Cargando...</div>;
  const { municipios, apps } = data;

  return (
    <div>
      <div className="mb-4 flex items-center gap-2 text-sm text-gray-600">
        <Map size={16} className="text-[#007934]" />
        <span>Cobertura por municipio: <strong>{municipios.length} municipios</strong> · <strong>{apps.length} aplicaciones</strong></span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        <table className="text-xs min-w-full" data-testid="matriz-municipios">
          <thead>
            <tr className="bg-[#007934] text-white">
              <th className="sticky left-0 bg-[#007934] text-left px-3 py-2.5 font-semibold min-w-[160px] z-10">
                Municipio
              </th>
              {apps.map(app => (
                <th key={app.app} className="px-2 py-2.5 font-medium min-w-[90px] max-w-[90px] text-center leading-tight" title={app.app}>
                  <div className="truncate max-w-[80px] mx-auto">{app.app}</div>
                  <div className={`w-2 h-2 rounded-full mx-auto mt-1 ${(IRA_CONFIG[app.ira_tipo] || IRA_CONFIG["Solución local"]).dot} opacity-80`} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {municipios.map((mun, mi) => {
              const row_count = apps.filter(app => app.cobertura[mun]).length;
              return (
                <tr key={mun} className={mi % 2 === 0 ? "bg-white hover:bg-gray-50" : "bg-gray-50/60 hover:bg-gray-100/60"}>
                  <td className="sticky left-0 bg-inherit px-3 py-2 font-medium text-gray-800 border-r border-gray-200 z-10">
                    <div className="flex items-center gap-2">
                      <span>{mun}</span>
                      <span className="text-[9px] text-gray-400 bg-gray-100 px-1 rounded">{row_count}</span>
                    </div>
                  </td>
                  {apps.map(app => (
                    <td key={app.app} className="px-2 py-2 text-center">
                      {app.cobertura[mun] ? (
                        <CheckCircle2 size={13} className="text-emerald-500 mx-auto" />
                      ) : (
                        <span className="text-gray-200 text-[10px] mx-auto block text-center">—</span>
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Resumen IRA ───────────────────────────────────────────────────────────────
function ResumenIRA({ stats }) {
  if (!stats || !stats.total_apps) return <div className="flex items-center justify-center py-20 text-gray-400"><RefreshCw className="animate-spin mr-2" size={16} />Cargando...</div>;

  const tiposData = [
    { tipo: "Producto reusable",      count: stats.ira_tipos?.["Producto reusable"] || 0,       cfg: IRA_CONFIG["Producto reusable"] },
    { tipo: "Reusable con variantes", count: stats.ira_tipos?.["Reusable con variantes"] || 0,  cfg: IRA_CONFIG["Reusable con variantes"] },
    { tipo: "Solución local",         count: stats.ira_tipos?.["Solución local"] || 0,          cfg: IRA_CONFIG["Solución local"] },
  ];

  const tecData = Object.entries(stats.tecnologias || {}).sort((a, b) => b[1] - a[1]);

  const lineasOrdered = (stats.por_linea || []).sort((a, b) => {
    const oa = LINEA_ORDER.indexOf(a.nombre);
    const ob = LINEA_ORDER.indexOf(b.nombre);
    return (oa === -1 ? 99 : oa) - (ob === -1 ? 99 : ob);
  });

  return (
    <div className="space-y-6" data-testid="resumen-ira">
      {/* KPIs globales */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Aplicaciones activas", value: stats.total_apps, icon: Monitor, color: "text-blue-600", bg: "bg-blue-50" },
          { label: "Municipios cubiertos", value: stats.total_municipios, icon: Map, color: "text-teal-600", bg: "bg-teal-50" },
          { label: "IRA Medio global", value: `${stats.ira_medio_global}/100`, icon: TrendingUp, color: "text-emerald-600", bg: "bg-emerald-50" },
          { label: "Coste anual total est.", value: fmtEur(stats.coste_anual_total), icon: Euro, color: "text-amber-600", bg: "bg-amber-50", note: "Portfolio completo (22 apps)" },
        ].map(kpi => (
          <div key={kpi.label} className={`${kpi.bg} rounded-xl p-4 border border-white`} data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-center gap-2 mb-1">
              <kpi.icon size={16} className={kpi.color} />
              <p className="text-xs text-gray-500">{kpi.label}</p>
            </div>
            <p className={`text-xl font-bold ${kpi.color}`}>{kpi.value}</p>
            {kpi.note && <p className="text-[10px] text-gray-400 mt-0.5">{kpi.note}</p>}
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Distribución IRA */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 text-sm mb-4 flex items-center gap-2">
            <BarChart2 size={15} className="text-[#007934]" />
            Distribución por Índice IRA
          </h3>
          <div className="space-y-3">
            {tiposData.map(td => (
              <div key={td.tipo}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${td.cfg.color}`}>{td.tipo}</span>
                  <span className="text-sm font-bold text-gray-800">{td.count} apps</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${td.cfg.bar}`}
                    style={{ width: `${(td.count / stats.total_apps) * 100}%` }}
                  />
                </div>
                <p className="text-[10px] text-gray-400 mt-0.5">{Math.round((td.count / stats.total_apps) * 100)}% del portfolio</p>
              </div>
            ))}
          </div>
          {/* Insight */}
          <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-xs text-amber-800">
              <strong>Oportunidad:</strong> {stats.ira_tipos?.["Solución local"] || 0} apps ({Math.round(((stats.ira_tipos?.["Solución local"] || 0) / stats.total_apps) * 100)}%) son soluciones locales con alto coste de réplica. La estandarización podría reducir costes de despliegue un 70-85%.
            </p>
          </div>
        </div>

        {/* Stack tecnológico */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 text-sm mb-4 flex items-center gap-2">
            <Code2 size={15} className="text-[#007934]" />
            Stack Tecnológico
          </h3>
          <div className="space-y-3">
            {tecData.map(([tec, count]) => (
              <div key={tec}>
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${TEC_BADGE[tec] || "bg-gray-100 text-gray-700"}`}>{tec}</span>
                  <span className="text-sm font-bold text-gray-800">{count} apps</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div
                    className="h-2 rounded-full bg-indigo-400 transition-all"
                    style={{ width: `${(count / stats.total_apps) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-xs text-blue-800">
              <strong>Modernización:</strong> ~50% del portfolio usa tecnología legacy. La migración a .NET correlaciona directamente con IRA más altos (≥90 vs ≤15).
            </p>
          </div>
        </div>
      </div>

      {/* Por línea de servicio */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-900 text-sm mb-4 flex items-center gap-2">
          <Layers size={15} className="text-[#007934]" />
          Análisis por Línea de Servicio
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {lineasOrdered.map(ls => {
            const linea = LINEA_COLORS[ls.nombre] || LINEA_COLORS["Transversal"];
            const iraClass = ls.ira_medio >= 90 ? "text-emerald-600" : ls.ira_medio >= 50 ? "text-blue-600" : "text-amber-600";
            return (
              <div key={ls.nombre} className={`${linea.bg} rounded-lg border ${linea.border} p-3`} data-testid={`linea-card-${ls.nombre}`}>
                <p className={`text-xs font-bold ${linea.text} mb-1`}>{ls.nombre}</p>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-lg font-bold text-gray-800">{ls.total}</p>
                    <p className="text-[10px] text-gray-500">aplicaciones</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-bold ${iraClass}`}>{ls.ira_medio}</p>
                    <p className="text-[10px] text-gray-500">IRA medio</p>
                  </div>
                </div>
                <div className="w-full bg-white/60 rounded-full h-1 mt-2">
                  <div
                    className={`h-1 rounded-full ${ls.ira_medio >= 90 ? "bg-emerald-500" : ls.ira_medio >= 50 ? "bg-blue-500" : "bg-amber-500"}`}
                    style={{ width: `${Math.min(ls.ira_medio, 100)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Impacto coste reusabilidad */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-900 text-sm mb-4 flex items-center gap-2">
          <Zap size={15} className="text-amber-500" />
          Impacto en Costes de Despliegue
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 size={16} className="text-emerald-600" />
              <span className="text-sm font-semibold text-emerald-800">App Reusable (IRA ≥ 90)</span>
            </div>
            <div className="space-y-1 text-sm text-emerald-700">
              <p>⏱ <strong>2–4 semanas</strong> para nueva población</p>
              <p>€ <strong>3.600 – 8.100€</strong> coste despliegue</p>
              <p>⚡ 80–180 horas de trabajo</p>
            </div>
          </div>
          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={16} className="text-red-600" />
              <span className="text-sm font-semibold text-red-800">Solución Local (IRA &lt; 20)</span>
            </div>
            <div className="space-y-1 text-sm text-red-700">
              <p>⏱ <strong>3–6 meses</strong> para nueva población</p>
              <p>€ <strong>18.000 – 28.800€</strong> coste despliegue</p>
              <p>⚡ 400–640 horas de trabajo</p>
            </div>
          </div>
        </div>
        <div className="mt-3 p-3 bg-gray-50 rounded-lg text-center">
          <p className="text-sm font-bold text-gray-800">Ahorro por reusabilidad: <span className="text-emerald-600">70% – 85%</span> en coste · Reducción tiempo: <span className="text-emerald-600">×5 más rápido</span></p>
        </div>
      </div>
    </div>
  );
}

// ── InnovationOS Tab ──────────────────────────────────────────────────────────
const STAGE_COLORS = {
  "ready_for_market":  { bg: "bg-emerald-100", text: "text-emerald-800", label: "Ready for Market" },
  "ready-for-market":  { bg: "bg-emerald-100", text: "text-emerald-800", label: "Ready for Market" },
  "en-produccion":     { bg: "bg-blue-100",    text: "text-blue-800",    label: "En Producción"    },
  "piloto":            { bg: "bg-amber-100",   text: "text-amber-800",   label: "Piloto"           },
  "scale":             { bg: "bg-purple-100",  text: "text-purple-800",  label: "Escalar"          },
  "default":           { bg: "bg-gray-100",    text: "text-gray-700",    label: ""                 },
};

function stageInfo(stage) {
  if (!stage) return STAGE_COLORS.default;
  const key = stage.toLowerCase().replace(/\s+/g, "-");
  return STAGE_COLORS[key] || { bg: "bg-gray-100", text: "text-gray-700", label: stage };
}

function InnovationOSCard({ item }) {
  const [expanded, setExpanded] = useState(false);
  const s = stageInfo(item.stage || item.estado || item.status);
  const name = item.name || item.nombre || item.title || "Sin nombre";
  const desc = item.description || item.descripcion || "";
  const cost = item.cost_estimated ?? item.cost ?? item.coste;
  const duration = item.duration_weeks ? `${item.duration_weeks} semanas` : item.duration || item.duracion;
  const portfolio = item.portfolio_url || item.portfolioUrl || item.link;
  const tags = item.technologies || item.tags || item.keywords || [];
  const hypothesis = item.hypothesis || "";
  const partner = item.partner || "";
  const decision = item.decision || "";

  const decisionBadge = decision === "scale"
    ? { bg: "bg-emerald-50", text: "text-emerald-700", label: "Escalar" }
    : decision === "stop"
    ? { bg: "bg-red-50", text: "text-red-700", label: "Parar" }
    : null;

  return (
    <div
      className="border border-gray-200 rounded-xl bg-white hover:shadow-md transition-shadow"
      data-testid={`innovation-os-card-${item.id || item._id || name}`}
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-[#007934]/10 flex items-center justify-center flex-shrink-0">
              <Globe size={14} className="text-[#007934]" />
            </div>
            <h3 className="font-semibold text-gray-900 text-sm leading-tight">{name}</h3>
          </div>
          <div className="flex flex-col items-end gap-1 flex-shrink-0">
            {s.label && (
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${s.bg} ${s.text}`}>
                {s.label}
              </span>
            )}
            {decisionBadge && (
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${decisionBadge.bg} ${decisionBadge.text}`}>
                {decisionBadge.label}
              </span>
            )}
          </div>
        </div>

        {desc && <p className="text-xs text-gray-600 line-clamp-2 mb-2">{desc}</p>}
        {partner && <p className="text-xs text-[#007934] font-medium mb-2">Partner: {partner}</p>}

        <div className="flex flex-wrap gap-2 mb-2">
          {cost != null && (
            <div className="flex items-center gap-1 text-xs text-gray-500 bg-gray-50 rounded-md px-2 py-1">
              <Euro size={11} />
              <span>{typeof cost === "number" ? `${cost.toLocaleString("es-ES")} €` : cost}</span>
            </div>
          )}
          {duration && (
            <div className="flex items-center gap-1 text-xs text-gray-500 bg-gray-50 rounded-md px-2 py-1">
              <Calendar size={11} />
              <span>{duration}</span>
            </div>
          )}
          {portfolio && (
            <a
              href={portfolio}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-[#007934] bg-[#007934]/10 rounded-md px-2 py-1 hover:bg-[#007934]/20"
              onClick={e => e.stopPropagation()}
            >
              <Link size={11} />
              Portfolio
            </a>
          )}
        </div>

        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {tags.slice(0, 5).map((t, i) => (
              <span key={i} className="text-xs bg-gray-100 text-gray-600 rounded px-1.5 py-0.5">#{t}</span>
            ))}
          </div>
        )}

        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-xs text-[#007934] hover:underline mt-1"
          data-testid={`expand-innovation-${item.id || name}`}
        >
          {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          {expanded ? "Ocultar detalle" : "Ver detalle"}
        </button>
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-2">
          {hypothesis && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Hipótesis</p>
              <p className="text-xs text-gray-700">{hypothesis}</p>
            </div>
          )}
          {item.decision_notes && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Notas decisión</p>
              <p className="text-xs text-gray-700">{item.decision_notes}</p>
            </div>
          )}
          {/* Remaining scalar fields */}
          {Object.entries(item)
            .filter(([k]) => !["id","_id","name","nombre","title","description","hypothesis",
              "decision_notes","stage","status","cost_estimated","cost","coste","duration_weeks",
              "duration","portfolio_url","technologies","tags","partner","decision","source",
              "created_at","updated_at","portfolio_project"].includes(k))
            .map(([k, v]) => v != null && typeof v !== "object" ? (
              <div key={k} className="flex gap-2 text-xs">
                <span className="text-gray-400 capitalize min-w-[90px]">{k.replace(/_/g, " ")}:</span>
                <span className="text-gray-700">{String(v)}</span>
              </div>
            ) : null)}
          <p className="text-xs text-gray-400 pt-1">
            Fuente: {item.source || "innovationos.valorizasm.com"}
          </p>
        </div>
      )}
    </div>
  );
}

function InnovationOSTab() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [stageFilter, setStageFilter] = useState("todos");
  const [searchQ, setSearchQ]   = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (force = false) => {
    if (force) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const params = force ? { force_refresh: true } : {};
      const res = await axios.get(`${API}/innovation-os/items`, { params });
      setData(res.data);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Error desconocido";
      setError(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];
  const cacheAge = data?.cache_age_seconds;

  const filtered = items.filter(item => {
    if (stageFilter !== "todos") {
      const s = (item.stage || item.estado || item.status || "").toLowerCase();
      // normalise: replace spaces+hyphens with underscores for comparison
      const sNorm = s.replace(/[-\s]+/g, "_");
      const filterNorm = stageFilter.replace(/[-\s]+/g, "_");
      if (sNorm !== filterNorm) return false;
    }
    if (searchQ) {
      const q = searchQ.toLowerCase();
      const name = (item.name || item.nombre || item.title || "").toLowerCase();
      const desc = (item.description || item.descripcion || "").toLowerCase();
      const tags = (item.tags || []).join(" ").toLowerCase();
      if (!name.includes(q) && !desc.includes(q) && !tags.includes(q)) return false;
    }
    return true;
  });

  const stages = [...new Set(items.map(i => i.stage || i.estado || i.status).filter(Boolean))];

  return (
    <div data-testid="innovation-os-tab">
      {/* Status bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5 p-3 bg-gray-50 rounded-xl border border-gray-200">
        <div className="flex items-center gap-2">
          {loading || refreshing ? (
            <Loader2 size={14} className="animate-spin text-gray-400" />
          ) : error ? (
            <WifiOff size={14} className="text-red-500" />
          ) : (
            <Wifi size={14} className="text-emerald-600" />
          )}
          <span className="text-xs text-gray-600 font-medium">
            {loading ? "Conectando con InnovationOS..." :
             error ? "Sin conexión con InnovationOS" :
             `Conectado · ${items.length} innovaciones`}
          </span>
          {cacheAge != null && !error && (
            <span className="text-xs text-gray-400">
              · actualizado hace {cacheAge < 60 ? `${cacheAge}s` : `${Math.round(cacheAge / 60)} min`}
            </span>
          )}
        </div>
        <button
          onClick={() => load(true)}
          disabled={loading || refreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#007934] text-white rounded-lg hover:bg-[#005a27] disabled:opacity-50 transition-colors"
          data-testid="innovation-os-refresh-btn"
        >
          <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
          Actualizar
        </button>
      </div>

      {/* Error state */}
      {error && !loading && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-3">
            <WifiOff size={24} className="text-red-400" />
          </div>
          <p className="text-gray-700 font-semibold mb-1">No se pudo conectar con InnovationOS</p>
          <p className="text-sm text-gray-500 max-w-md">{error}</p>
          <button
            onClick={() => load(true)}
            className="mt-4 px-4 py-2 text-sm bg-[#007934] text-white rounded-lg hover:bg-[#005a27]"
          >
            Reintentar
          </button>
        </div>
      )}

      {/* Filters */}
      {!error && !loading && items.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              placeholder="Buscar innovación..."
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#007934]"
              data-testid="innovation-os-search"
            />
            {searchQ && (
              <button onClick={() => setSearchQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
                <X size={12} />
              </button>
            )}
          </div>

          <div className="flex gap-1">
            {[{ id: "todos", label: "Todos" },
              ...stages.map(s => ({ id: s.toLowerCase().replace(/\s+/g, "-"), label: s }))
            ].map(opt => (
              <button
                key={opt.id}
                onClick={() => setStageFilter(opt.id)}
                className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
                  stageFilter === opt.id
                    ? "bg-[#007934] text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                data-testid={`stage-filter-${opt.id}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <Loader2 className="animate-spin mr-2" size={16} /> Cargando innovaciones...
        </div>
      )}

      {/* Cards grid */}
      {!loading && !error && filtered.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((item, i) => (
            <InnovationOSCard key={item.id || item._id || i} item={item} />
          ))}
        </div>
      )}

      {/* Empty */}
      {!loading && !error && items.length === 0 && (
        <div className="text-center py-16">
          <Globe size={36} className="text-gray-200 mx-auto mb-3" />
          <p className="text-gray-500">No se encontraron innovaciones en InnovationOS</p>
        </div>
      )}

      {/* No match */}
      {!loading && !error && items.length > 0 && filtered.length === 0 && (
        <div className="text-center py-12">
          <Search size={28} className="text-gray-200 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">Sin resultados para los filtros aplicados</p>
        </div>
      )}

      {/* Footer */}
      {!loading && !error && items.length > 0 && (
        <div className="mt-6 p-3 bg-gray-50 rounded-xl border border-gray-200 text-xs text-gray-500 flex items-center gap-2">
          <Globe size={12} />
          Fuente: <strong>innovationos.valorizasm.com</strong> — modo solo lectura
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function AppCatalog() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("catalogo");
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [filterLinea, setFilterLinea] = useState("");
  const [filterIra, setFilterIra] = useState("");
  const [filterTec, setFilterTec] = useState("");
  const [grouped, setGrouped] = useState(true);

  // CRUD modal state
  const [showModal, setShowModal] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  const fetchApps = useCallback(async () => {
    try {
      const params = {};
      if (filterLinea) params.linea_servicio = filterLinea;
      if (filterIra) params.ira_tipo = filterIra;
      if (filterTec) params.tecnologia = filterTec;
      if (searchQ) params.q = searchQ;
      const res = await axios.get(`${API}/catalogo-apps`, { params });
      setApps(res.data || []);
    } catch (e) {
      console.error(e);
    }
  }, [filterLinea, filterIra, filterTec, searchQ]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/catalogo-apps/stats`);
      setStats(res.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchMatrix = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/catalogo-apps/matrix`);
      setMatrix(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchApps(), fetchStats(), fetchMatrix()]).finally(() => setLoading(false));
  }, [fetchApps, fetchStats, fetchMatrix]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await axios.post(`${API}/catalogo-apps/seed`);
      await Promise.all([fetchApps(), fetchStats(), fetchMatrix()]);
    } finally { setSeeding(false); }
  };

  const handleEdit = (app) => { setEditingApp(app); setShowModal(true); };
  const handleNewApp = () => { setEditingApp(EMPTY_FORM); setShowModal(true); };
  const handleModalClose = () => { setShowModal(false); setEditingApp(null); };
  const handleModalSaved = async () => {
    handleModalClose();
    await Promise.all([fetchApps(), fetchStats(), fetchMatrix()]);
  };
  const handleDeleteRequest = (app) => setShowDeleteConfirm(app);
  const handleDeleteConfirm = async () => {
    if (!showDeleteConfirm) return;
    try {
      await axios.delete(`${API}/catalogo-apps/${showDeleteConfirm.id}`);
      setShowDeleteConfirm(null);
      await Promise.all([fetchApps(), fetchStats(), fetchMatrix()]);
    } catch (e) { console.error(e); }
  };

  // Agrupadas por línea
  const appsGrouped = LINEA_ORDER.reduce((acc, linea) => {
    const group = apps.filter(a => a.linea_servicio === linea);
    if (group.length > 0) acc[linea] = group;
    return acc;
  }, {});
  // Extra lines not in LINEA_ORDER
  apps.forEach(a => {
    if (!LINEA_ORDER.includes(a.linea_servicio) && !appsGrouped[a.linea_servicio]) {
      appsGrouped[a.linea_servicio] = apps.filter(ap => ap.linea_servicio === a.linea_servicio);
    }
  });

  const activeFilters = [filterLinea, filterIra, filterTec].filter(Boolean).length;

  const TABS = [
    { id: "catalogo", label: "Catálogo",         icon: Grid3X3 },
    { id: "matriz",   label: "Matriz Municipios", icon: Map },
    { id: "ira",      label: "Resumen IRA",        icon: BarChart2 },
    { id: "innovation-os", label: "InnovationOS",  icon: Globe },
  ];

  return (
    <div className="space-y-6" data-testid="app-catalog-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#007934] flex items-center justify-center">
              <Monitor size={18} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Catálogo de Aplicaciones</h1>
              <p className="text-sm text-gray-500">Portfolio tecnológico Valoriza Servicios Medioambientales</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {stats && (
            <div className="flex items-center gap-3 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5">
              <span className="font-bold text-[#007934]">{stats.total_apps}</span> apps ·
              <span className="font-bold text-[#007934]">{stats.total_municipios}</span> municipios ·
              <span className="font-bold text-[#007934]">{stats.lineas_servicio}</span> líneas
            </div>
          )}
          <button
            onClick={handleSeed}
            disabled={seeding}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#007934] text-white rounded-lg hover:bg-[#005a27] transition-colors disabled:opacity-50"
            data-testid="seed-apps-btn"
          >
            <RefreshCw size={14} className={seeding ? "animate-spin" : ""} />
            {seeding ? "Actualizando..." : "Actualizar datos"}
          </button>
          {isAdmin && (
            <button
              onClick={handleNewApp}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-[#007934] text-[#007934] rounded-lg hover:bg-[#E8F5E9] transition-colors font-medium"
              data-testid="new-app-btn"
            >
              <Plus size={14} /> Nueva app
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === t.id ? "bg-white text-[#007934] shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
            data-testid={`tab-${t.id}`}
          >
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Catálogo tab */}
      {tab === "catalogo" && (
        <div>
          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-5">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                placeholder="Buscar aplicación, proveedor..."
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#007934]"
                data-testid="catalog-search-input"
              />
              {searchQ && <button onClick={() => setSearchQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"><X size={12} /></button>}
            </div>

            <select
              value={filterLinea}
              onChange={e => setFilterLinea(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-[#007934] bg-white"
              data-testid="filter-linea"
            >
              <option value="">Todas las líneas</option>
              {LINEA_ORDER.map(l => <option key={l} value={l}>{l}</option>)}
            </select>

            <select
              value={filterIra}
              onChange={e => setFilterIra(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-[#007934] bg-white"
              data-testid="filter-ira"
            >
              <option value="">Todos los tipos IRA</option>
              <option value="Producto reusable">Producto reusable</option>
              <option value="Reusable con variantes">Reusable con variantes</option>
              <option value="Solución local">Solución local</option>
            </select>

            <select
              value={filterTec}
              onChange={e => setFilterTec(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-[#007934] bg-white"
              data-testid="filter-tecnologia"
            >
              <option value="">Toda tecnología</option>
              <option value=".NET">.NET</option>
              <option value="Legacy">Legacy</option>
              <option value="Azure ML">Azure ML</option>
            </select>

            <button
              onClick={() => setGrouped(!grouped)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors ${grouped ? "bg-[#007934] text-white border-[#007934]" : "bg-white text-gray-600 border-gray-200 hover:border-[#007934]"}`}
              data-testid="toggle-grouped"
            >
              <Layers size={13} />
              {grouped ? "Agrupado" : "Lista"}
            </button>

            {activeFilters > 0 && (
              <button onClick={() => { setFilterLinea(""); setFilterIra(""); setFilterTec(""); setSearchQ(""); }}
                className="flex items-center gap-1 px-3 py-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100"
                data-testid="clear-filters-btn">
                <X size={11} /> Limpiar ({activeFilters})
              </button>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20 text-gray-400">
              <RefreshCw className="animate-spin mr-2" size={16} />Cargando catálogo...
            </div>
          ) : apps.length === 0 ? (
            <div className="text-center py-16">
              <Monitor size={40} className="text-gray-200 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No se encontraron aplicaciones</p>
              <button onClick={handleSeed} className="mt-3 text-sm text-[#007934] underline">Cargar datos del catálogo</button>
            </div>
          ) : grouped && !searchQ && !filterLinea && !filterIra && !filterTec ? (
            // Grouped view
            <div className="space-y-8">
              {Object.entries(appsGrouped).map(([linea, lineaApps]) => {
                const linea_cfg = LINEA_COLORS[linea] || LINEA_COLORS["Transversal"];
                const ira_medio = Math.round(lineaApps.reduce((s, a) => s + (a.ira_score || 0), 0) / lineaApps.length);
                return (
                  <div key={linea} data-testid={`linea-group-${linea}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`h-0.5 w-4 rounded ${linea_cfg.text.replace("text", "bg")}`} style={{ backgroundColor: linea_cfg.accent }} />
                      <h2 className={`text-sm font-bold ${linea_cfg.text} uppercase tracking-wide`}>{linea}</h2>
                      <span className="text-xs text-gray-400">{lineaApps.length} apps · IRA medio: {ira_medio}</span>
                      <div className="flex-1 h-px bg-gray-100" />
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                      {lineaApps.map(app => <AppCard key={app.id} app={app} onEdit={handleEdit} onDelete={handleDeleteRequest} isAdmin={isAdmin} />)}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Flat view
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {apps.map(app => <AppCard key={app.id} app={app} onEdit={handleEdit} onDelete={handleDeleteRequest} isAdmin={isAdmin} />)}
            </div>
          )}

          {/* Summary footer */}
          {!loading && apps.length > 0 && (
            <div className="mt-6 p-3 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between text-xs text-gray-500">
              <span>Mostrando <strong>{apps.length}</strong> aplicaciones</span>
              <span>Coste anual estimado total: <strong className="text-gray-700">{stats ? fmtEur(stats.coste_anual_total) : "—"}</strong></span>
            </div>
          )}
        </div>
      )}

      {/* Matriz tab */}
      {tab === "matriz" && <MatrizView data={matrix} />}

      {/* IRA tab */}
      {tab === "ira" && <ResumenIRA stats={stats} />}

      {/* InnovationOS tab */}
      {tab === "innovation-os" && <InnovationOSTab />}

      {/* ── CRUD Modal ───────────────────────────────────────────────────────── */}
      {showModal && (
        <AppFormModal
          app={editingApp}
          onClose={handleModalClose}
          onSaved={handleModalSaved}
        />
      )}

      {/* ── Delete Confirm ───────────────────────────────────────────────────── */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl" data-testid="delete-confirm-modal">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 size={18} className="text-red-600" />
              </div>
              <h3 className="text-base font-bold text-gray-900">Eliminar aplicación</h3>
            </div>
            <p className="text-sm text-gray-600 mb-5">
              ¿Seguro que quieres eliminar <strong>{showDeleteConfirm.nombre_corto || showDeleteConfirm.nombre}</strong>? Esta acción no se puede deshacer.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setShowDeleteConfirm(null)} className="flex-1 border border-gray-200 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={handleDeleteConfirm} className="flex-1 bg-red-500 text-white rounded-xl py-2.5 text-sm font-semibold hover:bg-red-600" data-testid="confirm-delete-btn">
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
